'use client';
import type { Placement } from '@floating-ui/react-dom';
import { computePosition, flip, shift } from '@floating-ui/react-dom';
import { type RefObject, useEffect, useRef } from 'react';

export interface DropdownHookProps {
	mode?: 'click' | 'hover';
	targetRef: RefObject<HTMLElement | null>;
	placement?: Placement;
	onChange?(showed: boolean): void;
}

function shouldHidePopoverOnClick(
	target: HTMLElement | null,
	popover: HTMLElement | null,
	event: MouseEvent
) {
	if (!target || !popover) {
		return false;
	}

	const path = event.composedPath();

	if (path.includes(target)) {
		return false;
	}

	if (path.includes(popover)) {
		for (let i = path.indexOf(popover) - 1, element: HTMLElement; i >= 0; i--) {
			element = path[i] as HTMLElement;

			if (element.dataset.coc) {
				return true;
			}
		}

		return false;
	}

	return true;
}

export function useDropdown({
	mode = 'click',
	targetRef,
	placement,
	onChange,
}: DropdownHookProps) {
	const popoverRef = useRef<HTMLDivElement | null>(null);
	const placementRef = useRef<Placement>(placement);
	const onChangeRef = useRef(onChange);

	onChangeRef.current = onChange;
	placementRef.current = placement;

	useEffect(() => {
		const target = targetRef.current;
		const popover = popoverRef.current;
		let opened = false;

		if (!target || !popover) {
			return;
		}

		const updatePosition = () => {
			if (!opened || !target || !popover) {
				return;
			}

			void computePosition(target, popover, {
				placement: placementRef.current,
				middleware: [flip(), shift({ padding: 5 })],
				strategy: 'fixed',
			}).then(({ x, y }) => {
				Object.assign(popover.style, {
					position: 'fixed',
					left: `${x}px`,
					top: `${y}px`,
				});
			});
		};

		const showPopover = () => {
			if (popover && !opened) {
				opened = true;
				popover.showPopover();
				try {
					updatePosition();
					onChangeRef.current?.(true);
				} catch (error) {
					popover.showPopover();
					onChangeRef.current?.(true);
				}
			}
		};

		const hidePopover = () => {
			if (popover && opened) {
				opened = false;
				popover.hidePopover();
				onChangeRef.current?.(false);
			}
		};

		const togglePopover = () => {
			if (opened) {
				hidePopover();
			} else {
				showPopover();
			}
		};

		let timeout: ReturnType<typeof setTimeout> | null = null;

		const debouncedShowPopover = () => {
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}

			showPopover();
		};

		const debouncedHidePopover = () => {
			if (timeout) {
				clearTimeout(timeout);
				timeout = null;
			}

			timeout = setTimeout(() => {
				hidePopover();
			}, 300);
		};

		const onDocumentClick = (event: MouseEvent) => {
			if (opened && shouldHidePopoverOnClick(target, popover, event)) {
				hidePopover();
			}
		};

		const onScroll = () => {
			if (opened) {
				updatePosition();
			}
		};

		if (mode === 'click') {
			target.addEventListener('click', togglePopover);
		} else {
			target.addEventListener('mouseenter', debouncedShowPopover);
			popover.addEventListener('mouseenter', debouncedShowPopover);
			target.addEventListener('mouseleave', debouncedHidePopover);
			popover.addEventListener('mouseleave', debouncedHidePopover);

			target.addEventListener('focus', debouncedShowPopover);
			popover.addEventListener('focus', debouncedShowPopover, true);
			target.addEventListener('blur', debouncedHidePopover);
			popover.addEventListener('blur', debouncedHidePopover, true);
		}

		document.addEventListener('click', onDocumentClick);
		window.addEventListener('scroll', onScroll, true);

		return () => {
			if (mode === 'click') {
				target.removeEventListener('click', togglePopover);
			} else {
				target.removeEventListener('mouseenter', debouncedShowPopover);
				popover.removeEventListener('mouseenter', debouncedShowPopover);
				target.removeEventListener('mouseleave', debouncedHidePopover);
				popover.removeEventListener('mouseleave', debouncedHidePopover);

				target.removeEventListener('focus', debouncedShowPopover);
				popover.removeEventListener('focus', debouncedShowPopover, true);
				target.removeEventListener('blur', debouncedHidePopover);
				popover.removeEventListener('blur', debouncedHidePopover, true);
			}

			document.removeEventListener('click', onDocumentClick);
			window.removeEventListener('scroll', onScroll, true);

			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [mode, targetRef]);

	return popoverRef;
}
